import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { BadgeCheck, RotateCcw, Sparkles } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  eventSettingsQueryOptions,
  patchEvent,
  type EventSettingsRow,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  SettingsPage,
  SettingsPageFooter,
  TextInput,
} from "@/components/settings/primitives"

// Invite & guest-email copy + the white-label branding status.
//
// The inputs are PRE-FILLED with the resolved smart defaults (which adapt
// to this event's features — no "pick your activities" when the schedule
// is off), so organizers edit real copy in place. On save, only fields
// that DIFFER from their default are stored as overrides — untouched
// fields keep tracking the defaults if the event's features change later.
// Stored as one JSON blob on events.inviteCopy; the server sanitizes on
// read (quickback events/lib/invite-copy.ts — keep the default strings
// below in sync with that file).

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/invites")({
  component: InvitesView,
})

type CopyForm = {
  personalMessage: string // multi-line; empty = no message
  welcomeLine: string
  introParagraph: string
  oneLastStep: string
  benefits: string // one per line
  step3Label: string
  ctaLabel: string
  teamLabel: string
  // Rich invite email — step + CTA copy
  getStartedLabel: string
  step1Label: string
  appBlurb: string // multi-line
  downloadCtaLabel: string
  step2Label: string
  step2Body: string // multi-line
  declineLabel: string
  // Framed hero headlines (per audience)
  confirmHeadline: string
  goingHeadline: string
  waitlistHeadline: string
  plusOneHeadline: string
}

const FIELDS: (keyof CopyForm)[] = [
  "personalMessage",
  "welcomeLine",
  "introParagraph",
  "oneLastStep",
  "benefits",
  "step3Label",
  "ctaLabel",
  "teamLabel",
  "getStartedLabel",
  "step1Label",
  "appBlurb",
  "downloadCtaLabel",
  "step2Label",
  "step2Body",
  "declineLabel",
  "confirmHeadline",
  "goingHeadline",
  "waitlistHeadline",
  "plusOneHeadline",
]

const MAX_LINE = 300
const MAX_MESSAGE = 1000
// Fields that allow a longer, multi-line value (higher cap, textarea input).
const LONG_FIELDS: (keyof CopyForm)[] = ["personalMessage", "appBlurb", "step2Body"]

// Mirror of the server's flag-aware defaults (quickback
// events/lib/invite-copy.ts) using this event's real data — what actually
// renders when no override is saved.
function resolveDefaults(row: EventSettingsRow): CopyForm {
  const scheduleOn = (row.enableSchedule ?? 1) === 1
  const travelOn = (row.enableTravel ?? 1) === 1
  const conciergeOn = (row.enableConcierge ?? 1) === 1
  const host = row.hostName?.trim() || "The organizers"

  const benefits: string[] = []
  if (scheduleOn) {
    benefits.push("Your full schedule & itinerary", "Pick your activities")
  } else {
    benefits.push("Event details & updates")
  }
  if (travelOn) benefits.push("Your flights & transfers")
  if (conciergeOn) benefits.push("Message the events team")

  return {
    // No default personal message — it's an optional extra note the organizer
    // opts into. Empty means the invite shows no personal line (unless a guest
    // has their own per-guest message).
    personalMessage: "",
    // The lander greets each guest personally — {first name} is filled per
    // guest at render time.
    welcomeLine: "Hi {first name} — you're invited",
    introParagraph: `${host} has invited you to ${row.name}.`,
    oneLastStep: scheduleOn
      ? "There's one last step — confirm your spot in the app and pick your activities."
      : "There's one last step — confirm your spot in the app.",
    benefits: benefits.slice(0, 4).join("\n"),
    step3Label: scheduleOn ? "RSVP & pick your activities" : "RSVP & see event details",
    ctaLabel: "Confirm your spot in the app",
    teamLabel: "Event team",
    // Rich invite email — step + CTA copy (mirrors compose-invite.ts defaults).
    getStartedLabel: "Get started",
    step1Label: "Get the Attend Events app",
    appBlurb: scheduleOn
      ? "Download the Attend app to confirm your spot, complete your details, view your itinerary, make activity selections, get updates, and more."
      : "Download the Attend app to confirm your spot, complete your details, get updates, and more.",
    downloadCtaLabel: "Download Now",
    step2Label: "RSVP & join the event",
    step2Body: "Scan the QR code in the app, or enter your invite code to RSVP.",
    declineLabel: "Can't make it?",
    // Framed hero headlines (per audience).
    confirmHeadline: "One last step to confirm your spot",
    goingHeadline: "You're going",
    waitlistHeadline: "You're on the waitlist",
    plusOneHeadline: "{inviter} wants you to join them",
  }
}

// Overrides stored on the event, mapped onto the form shape (missing →
// empty string, meaning "no override").
function parseOverrides(raw: string | null | undefined): Partial<CopyForm> {
  if (!raw) return {}
  try {
    const j = JSON.parse(raw) as Record<string, unknown>
    const line = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined)
    return {
      personalMessage: line(j.personalMessage),
      welcomeLine: line(j.welcomeLine),
      introParagraph: line(j.introParagraph),
      oneLastStep: line(j.oneLastStep),
      benefits: Array.isArray(j.benefits)
        ? j.benefits.filter((b) => typeof b === "string").join("\n") || undefined
        : undefined,
      step3Label: line(j.step3Label),
      ctaLabel: line(j.ctaLabel),
      teamLabel: line(j.teamLabel),
      getStartedLabel: line(j.getStartedLabel),
      step1Label: line(j.step1Label),
      appBlurb: line(j.appBlurb),
      downloadCtaLabel: line(j.downloadCtaLabel),
      step2Label: line(j.step2Label),
      step2Body: line(j.step2Body),
      declineLabel: line(j.declineLabel),
      confirmHeadline: line(j.confirmHeadline),
      goingHeadline: line(j.goingHeadline),
      waitlistHeadline: line(j.waitlistHeadline),
      plusOneHeadline: line(j.plusOneHeadline),
    }
  } catch {
    return {}
  }
}

// Persist ONLY the fields that differ from their resolved default; a field
// left at (or cleared back to) the default stores nothing and keeps
// tracking the smart default.
function serializeDiff(form: CopyForm, defaults: CopyForm): string | null {
  const out: Record<string, unknown> = {}
  for (const key of FIELDS) {
    const value = form[key].trim()
    const fallback = defaults[key].trim()
    if (!value || value === fallback) continue
    if (key === "benefits") {
      const lines = value
        .split("\n")
        .map((b) => b.trim())
        .filter(Boolean)
        .slice(0, 6)
      if (lines.length && lines.join("\n") !== fallback) out.benefits = lines
    } else {
      out[key] = value
    }
  }
  return Object.keys(out).length ? JSON.stringify(out) : null
}

function whiteLabelEnabled(row: EventSettingsRow | undefined): boolean {
  const raw = row?.featureFlags
  if (!raw) return false
  try {
    const flags = typeof raw === "string" ? JSON.parse(raw) : raw
    return !!(flags as any)?.whiteLabel?.enabled
  } catch {
    return false
  }
}

function InvitesView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<CopyForm | null>(null)
  const [defaults, setDefaults] = useState<CopyForm | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!q.data || dirty) return
    const d = resolveDefaults(q.data)
    const overrides = parseOverrides(q.data.inviteCopy)
    setDefaults(d)
    setForm({
      personalMessage: overrides.personalMessage ?? d.personalMessage,
      welcomeLine: overrides.welcomeLine ?? d.welcomeLine,
      introParagraph: overrides.introParagraph ?? d.introParagraph,
      oneLastStep: overrides.oneLastStep ?? d.oneLastStep,
      benefits: overrides.benefits ?? d.benefits,
      step3Label: overrides.step3Label ?? d.step3Label,
      ctaLabel: overrides.ctaLabel ?? d.ctaLabel,
      teamLabel: overrides.teamLabel ?? d.teamLabel,
      getStartedLabel: overrides.getStartedLabel ?? d.getStartedLabel,
      step1Label: overrides.step1Label ?? d.step1Label,
      appBlurb: overrides.appBlurb ?? d.appBlurb,
      downloadCtaLabel: overrides.downloadCtaLabel ?? d.downloadCtaLabel,
      step2Label: overrides.step2Label ?? d.step2Label,
      step2Body: overrides.step2Body ?? d.step2Body,
      declineLabel: overrides.declineLabel ?? d.declineLabel,
      confirmHeadline: overrides.confirmHeadline ?? d.confirmHeadline,
      goingHeadline: overrides.goingHeadline ?? d.goingHeadline,
      waitlistHeadline: overrides.waitlistHeadline ?? d.waitlistHeadline,
      plusOneHeadline: overrides.plusOneHeadline ?? d.plusOneHeadline,
    })
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form || !defaults) throw new Error("Not ready")
      // One-line fields (everything except benefits + the long multi-line ones)
      // are capped at MAX_LINE; the long fields get the higher MAX_MESSAGE cap.
      const tooLong = FIELDS.find(
        (key) =>
          key !== "benefits" &&
          !LONG_FIELDS.includes(key) &&
          form[key].trim().length > MAX_LINE,
      )
      if (tooLong) throw new ApiError(`Keep each line under ${MAX_LINE} characters`, 400)
      const longTooLong = LONG_FIELDS.find((key) => form[key].trim().length > MAX_MESSAGE)
      if (longTooLong) throw new ApiError(`Keep that message under ${MAX_MESSAGE} characters`, 400)
      return patchEvent(eventId, { inviteCopy: serializeDiff(form, defaults) })
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Invite copy saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to save"),
  })

  const update = (patch: Partial<CopyForm>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  const whiteLabel = whiteLabelEnabled(q.data)

  // Which fields currently differ from their default — drives the per-field
  // "customized" affordance + reset.
  const customized = (key: keyof CopyForm) =>
    !!form && !!defaults && form[key].trim() !== defaults[key].trim()

  const resetField = (key: keyof CopyForm) => {
    if (!defaults) return
    update({ [key]: defaults[key] } as Partial<CopyForm>)
  }

  const fieldLabel = (
    label: string,
    key: keyof CopyForm,
  ): React.ReactNode => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {customized(key) && (
        <>
          <span className="rounded bg-[var(--accent)]/10 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[var(--accent)] uppercase">
            Customized
          </span>
          <button
            type="button"
            title="Reset to default"
            onClick={() => resetField(key)}
            className="text-plum-700/40 transition hover:text-plum-700 dark:text-plum-200/30 dark:hover:text-plum-200"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        </>
      )}
    </span>
  )

  return (
    <SettingsPage
      title="Invites & guest emails"
      description="What guests read before they're in the app — the invite email, lifecycle nudges, and the invite landing page. Edit any line; untouched lines keep the smart default, which adapts to this event's features."
    >
      <Card title="Branding mode">
        <div className="flex items-start gap-3">
          <span
            className={
              whiteLabel
                ? "mt-0.5 rounded-md bg-emerald-100 p-1.5 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300"
                : "mt-0.5 rounded-md bg-plum-100/70 p-1.5 text-plum-700/60 dark:bg-white/5 dark:text-plum-200/45"
            }
          >
            {whiteLabel ? <BadgeCheck className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
              {whiteLabel ? "White-label — your brand leads" : "Attend branding"}
            </p>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
              {whiteLabel
                ? "Guest emails and the invite page carry your event's brand — Attend appears only as the app name and a delivery line. Managed by Attend as part of your package."
                : "Guest emails and the invite page include Attend branding. White-label (your brand only) is available as a premium upgrade — contact us to enable it for this event."}
            </p>
          </div>
        </div>
      </Card>

      <Card title="Invite copy">
        <Field
          label={fieldLabel("Personal message", "personalMessage")}
          hint="An optional note shown near the top of every invite email, just under the greeting. Leave blank for none. A per-guest message overrides this."
        >
          <textarea
            value={form?.personalMessage ?? ""}
            disabled={!form}
            rows={3}
            placeholder="e.g. We're so excited to have you join us — it won't be the same without you!"
            onChange={(e) => update({ personalMessage: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Welcome line", "welcomeLine")}
          hint="The greeting on the invite landing page. {first name} fills in per guest."
        >
          <TextInput
            value={form?.welcomeLine ?? ""}
            disabled={!form}
            onChange={(e) => update({ welcomeLine: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Intro paragraph", "introParagraph")}
          hint="The first line of the invite email."
        >
          <TextInput
            value={form?.introParagraph ?? ""}
            disabled={!form}
            onChange={(e) => update({ introParagraph: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("One last step line", "oneLastStep")}
          hint="The emphasized instruction in invite and confirmation emails."
        >
          <TextInput
            value={form?.oneLastStep ?? ""}
            disabled={!form}
            onChange={(e) => update({ oneLastStep: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("App benefits", "benefits")}
          hint="One per line, up to 6 — the checklist of what guests get in the app."
        >
          <textarea
            value={form?.benefits ?? ""}
            disabled={!form}
            rows={4}
            onChange={(e) => update({ benefits: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Landing-page step 3", "step3Label")}
          hint='The final step on the invite page, after "Get the Attend app".'
        >
          <TextInput
            value={form?.step3Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step3Label: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Button label", "ctaLabel")}
          hint="The main call-to-action in emails."
        >
          <TextInput
            value={form?.ctaLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ ctaLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Team label", "teamLabel")}
          hint='The footer prefix — "Event team: {host}".'
        >
          <TextInput
            value={form?.teamLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ teamLabel: e.target.value })}
          />
        </Field>
      </Card>

      <Card title="Email steps & buttons">
        <Field
          label={fieldLabel("Section eyebrow", "getStartedLabel")}
          hint='The small label above the two-step block in the invite email — default "Get started".'
        >
          <TextInput
            value={form?.getStartedLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ getStartedLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 1 header", "step1Label")}
          hint='The "1." step heading — default "Get the Attend Events app".'
        >
          <TextInput
            value={form?.step1Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step1Label: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("App blurb", "appBlurb")}
          hint="The paragraph under step 1 describing what the app does."
        >
          <textarea
            value={form?.appBlurb ?? ""}
            disabled={!form}
            rows={3}
            onChange={(e) => update({ appBlurb: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Download button", "downloadCtaLabel")}
          hint='The step 1 download button — default "Download Now".'
        >
          <TextInput
            value={form?.downloadCtaLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ downloadCtaLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 2 header", "step2Label")}
          hint='The "2." step heading — default "RSVP & join the event".'
        >
          <TextInput
            value={form?.step2Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step2Label: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 2 instruction", "step2Body")}
          hint="The line above the invite code / QR box telling guests how to RSVP."
        >
          <textarea
            value={form?.step2Body ?? ""}
            disabled={!form}
            rows={2}
            onChange={(e) => update({ step2Body: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Decline link", "declineLabel")}
          hint={`The link under the code box for guests who can't attend — default "Can't make it?".`}
        >
          <TextInput
            value={form?.declineLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ declineLabel: e.target.value })}
          />
        </Field>
      </Card>

      <Card title="Email headlines">
        <p className="-mt-1 mb-1 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          The large hero headline at the top of the invite email. Each guest sees the
          one that matches where they are — a fresh invite, a pending confirmation, already
          going, or waitlisted.
        </p>
        <Field
          label={fieldLabel("Confirm your spot", "confirmHeadline")}
          hint="Shown to guests who still need to confirm — default “One last step to confirm your spot”."
        >
          <TextInput
            value={form?.confirmHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ confirmHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Going", "goingHeadline")}
          hint="Shown to guests who've already confirmed — default “You're going”."
        >
          <TextInput
            value={form?.goingHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ goingHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Waitlist", "waitlistHeadline")}
          hint="Shown to waitlisted guests — default “You're on the waitlist”."
        >
          <TextInput
            value={form?.waitlistHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ waitlistHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Plus-one invite", "plusOneHeadline")}
          hint="Shown when one guest invites another. Use {inviter} for the host's name — default “{inviter} wants you to join them”."
        >
          <TextInput
            value={form?.plusOneHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ plusOneHeadline: e.target.value })}
          />
        </Field>
        <div className="pt-1">
          <GhostButton
            type="button"
            disabled={!form || !defaults || FIELDS.every((k) => !customized(k))}
            onClick={() => {
              if (defaults) {
                setDirty(true)
                setForm({ ...defaults })
              }
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset all to defaults
          </GhostButton>
        </div>
      </Card>

      <SettingsPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SettingsPageFooter>
    </SettingsPage>
  )
}
